import { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

export const SessionContext = createContext();

export const SessionProvider = ({ children }) => {
    const [sessionId, setSessionId] = useState(() => localStorage.getItem('sessionId'));
    const [sessionExpires, setSessionExpires] = useState(() => localStorage.getItem('sessionExpires'));
    const [accessToken, setAccessToken] = useState(() => localStorage.getItem('accessToken'));
    const [currentUser, setCurrentUser] = useState(() => {
        const user = localStorage.getItem('currentUser');
        return user ? JSON.parse(user) : null;
    });
    // Determine if the session is still valid based on expiration time
    const isSessionValid = sessionExpires ? new Date(sessionExpires) > new Date() : false;
    const [isAuthenticated, setIsAuthenticated] = useState(isSessionValid && !!sessionId);

    // Clear localStorage and state if session is invalid
    const clearInvalidSession = () => {
        localStorage.removeItem('sessionId');
        localStorage.removeItem('sessionExpires');
        localStorage.removeItem('currentUser');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('customerId');
        localStorage.removeItem('activeStore');

        // Reset state
        setSessionId(null);
        setSessionExpires(null);
        setCurrentUser(null);
        setIsAuthenticated(false);
        setAccessToken(null);
    };

    // TanStack Query for user fetch
    const fetchUser = async () => {
        const storedSessionId = localStorage.getItem('sessionId');
        if (!storedSessionId) throw new Error('No session');
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/auth/me`, {
            headers: { 'X-Session-Id': storedSessionId },
            withCredentials: true,
        });
        return response.data.data;
    };

    const {
        data: authData,
        isSuccess,
        refetch: refetchUser,
    } = useQuery({
        queryKey: ['authMe'],
        queryFn: fetchUser,
        enabled: !!sessionId,
        retry: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    const login = async ({ sessionId: newSessionId, sessionExpires: newSessionExpires, accessToken: newAccessToken, customerId }) => {
        if (newSessionId) {
            localStorage.setItem('sessionId', newSessionId);
            localStorage.setItem('sessionExpires', newSessionExpires);
            setSessionId(newSessionId);
            setSessionExpires(newSessionExpires);
            // Manually fetch user after login
            await refetchUser();
        }
        if (newAccessToken) {
            localStorage.setItem('accessToken', newAccessToken);
            setAccessToken(newAccessToken);
        }

        if (customerId) {
            // Optionally store customerId if needed
            localStorage.setItem('customerId', customerId);
        }
    };

    const logout = async () => {
        const storedSessionId = localStorage.getItem('sessionId');
        if (storedSessionId) {
            try {
                await axios.post(
                    `${import.meta.env.VITE_API_URL}/auth/logout`,
                    {},
                    {
                        headers: {
                            'X-Session-Id': storedSessionId,
                        },
                        withCredentials: true,
                    }
                );
            } catch (err) {
                console.error('Logout failed:', err);
            }
        }
        clearInvalidSession();
    };

    const customerLogout = async () => {
        const storedAccessToken = localStorage.getItem('accessToken');
        if (storedAccessToken) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('customerId');
            setAccessToken(null);
        }
    };

    const updateSession = async ({ activeStore }) => {
        try {
            await axios.post(
                `${import.meta.env.VITE_API_URL}/auth/update-session`,
                { activeStore },
                {
                    headers: {
                        'X-Session-Id': sessionId,
                    },
                    withCredentials: true,
                }
            );

            // Optimistically update currentUser in state and localStorage
            const updatedUser = {
                ...currentUser,
                activeStore,
            };
            setCurrentUser(updatedUser);
            localStorage.setItem('currentUser', JSON.stringify(updatedUser));
        } catch (error) {
            console.error('Failed to update session:', error);
        }
    };

    // Function to set returnTo localStorage item the first time
    const setReturnTo = () => {
        const returnTo = localStorage.getItem('returnTo');
        if (returnTo) return;
        const currentUrl = window.location.pathname + window.location.search;
        localStorage.setItem('returnTo', currentUrl);
    };

    useEffect(() => {
        if (isSuccess && authData?.user) {
            setCurrentUser(authData.user);
            localStorage.setItem('currentUser', JSON.stringify(authData.user));
            setIsAuthenticated(true);
        }
    }, [isSuccess, authData]);

    // Set axios headers for session ID, sessionId isn't available until after login
    axios.defaults.headers.common['x-session-id'] = sessionId;

    return (
        <SessionContext.Provider
            value={{
                sessionId,
                accessToken,
                isAuthenticated,
                isCustomerAuthenticated: !!accessToken,
                login,
                logout,
                customerLogout,
                currentUser,
                setCurrentUser,
                refetchUser,
                updateSession,
                clearInvalidSession,
                setReturnTo,
            }}
        >
            {children}
        </SessionContext.Provider>
    );
};
